// Pointer input and 2D overlay for the local tools (§11.4 masks, §11.8 heal/clone, §11.10 skin, §11.11
// clean whites). Geometry lives in source-normalized coordinates; the controller converts to the view.
import type { PreviewController } from '../../engine/preview';
import { useEditor } from '../../state/editor';
import { useUi, type UiState } from '../../state/ui';
import {
  autoHealOffset,
  extendStroke,
  MAX_HEAL,
  newHealSpot,
  newStroke,
  strokeCentre,
} from '../../features/localTools';
import type { LinearGeom, LocalMask, RadialGeom, Settings, Stroke, StrokePoint } from '../../model/types';
import { cropIsCovered } from '../../engine/geometry';
import { cssVar } from '../../app/theme';

type Uv = { x: number; y: number };
export type LocalDrag =
  | { kind: 'paint'; target: 'mask' | 'skin' | 'cleanWhites' | 'heal' | 'refine'; stroke: Stroke }
  | { kind: 'linear'; which: 'a' | 'b' | 'move' | 'new'; start: Uv; geom: LinearGeom }
  | { kind: 'radial'; which: 'move' | 'rx' | 'ry' | 'rot' | 'new'; start: Uv; geom: RadialGeom }
  | { kind: 'healHandle'; index: number; which: 'dst' | 'src'; start: Uv; points: StrokePoint[]; offset: Uv }
  | { kind: 'cropPan'; x: number; y: number; crop: { x: number; y: number; w: number; h: number } };

export type LocalCtx = {
  ctrl: PreviewController;
  id: string;
  settings: Settings;
  ui: UiState;
};

const HIT = 10;
const copyStroke = (k: Stroke): Stroke => ({ ...k, points: k.points.map((p) => ({ ...p })) });
export const isLocalTool = (t: UiState['tool']) =>
  t === 'mask' ||
  t === 'heal' ||
  t === 'skin' ||
  t === 'cleanWhites' ||
  t === 'bgRefine' ||
  t === 'spot' ||
  t === 'cropPan';

export function selectedMask(s: Settings, ui: UiState): { mask: LocalMask; index: number } | null {
  const index = s.masks.findIndex((m) => m.id === ui.maskId);
  return index < 0 ? null : { mask: s.masks[index]!, index };
}

const dist = (a: [number, number] | null, x: number, y: number) =>
  a ? Math.hypot(a[0] - x, a[1] - y) : Infinity;

/** Radius of a stroke on screen (CSS px) at `uv`. */
export function screenRadius(ctrl: PreviewController, uv: Uv, size: number): number {
  const src = ctrl.sourceSize;
  if (!src) return 0;
  const r = (size / 2) * Math.min(src.width, src.height);
  const a = ctrl.sourceToView(uv.x, uv.y),
    b = ctrl.sourceToView(uv.x + r / src.width, uv.y);
  return a && b ? Math.hypot(b[0] - a[0], b[1] - a[1]) : 0;
}

function radialPoint(g: RadialGeom, W: number, H: number, lx: number, ly: number): Uv {
  const a = (g.angle * Math.PI) / 180;
  const cs = Math.cos(a),
    sn = Math.sin(a);
  return { x: g.cx + (cs * lx - sn * ly) / W, y: g.cy + (sn * lx + cs * ly) / H };
}
function radialHandles(g: RadialGeom, W: number, H: number) {
  return {
    rx: radialPoint(g, W, H, g.rx * W, 0),
    ry: radialPoint(g, W, H, 0, g.ry * H),
    rot: radialPoint(g, W, H, 0, -g.ry * H * 1.25),
  };
}

const pressure = (e: { pointerType: string; pressure: number }) =>
  e.pointerType === 'mouse' ? 1 : Math.max(0.1, e.pressure || 1);

export function localDown(
  c: LocalCtx,
  px: number,
  py: number,
  e: { altKey: boolean; pointerType: string; pressure: number },
): LocalDrag | null {
  const { ctrl, settings, ui, id } = c;
  const uv0 = ctrl.viewToSource(px, py);
  const src = ctrl.sourceSize;
  if (!src) return null;
  const W = src.width,
    H = src.height;
  const view = (u: Uv) => ctrl.sourceToView(u.x, u.y);
  const ed = useEditor.getState();

  if (ui.tool === 'heal') {
    // Existing spot handles first (draggable after creation).
    for (let i = settings.heal.length - 1; i >= 0; i--) {
      const sp = settings.heal[i]!;
      const cen = strokeCentre(sp.stroke);
      const srcC = { x: cen.x + sp.offset.x, y: cen.y + sp.offset.y };
      const hitR = Math.max(HIT, screenRadius(ctrl, cen, sp.stroke.size));
      if (dist(view(srcC), px, py) < hitR)
        return {
          kind: 'healHandle',
          index: i,
          which: 'src',
          start: srcC,
          points: sp.stroke.points,
          offset: sp.offset,
        };
      if (dist(view(cen), px, py) < hitR)
        return {
          kind: 'healHandle',
          index: i,
          which: 'dst',
          start: cen,
          points: sp.stroke.points,
          offset: sp.offset,
        };
    }
    if (!uv0) return null;
    if (e.altKey) {
      useUi.getState().set({ healSource: { x: uv0[0], y: uv0[1] } });
      return null;
    }
    if (settings.heal.length >= MAX_HEAL) return null;
    return {
      kind: 'paint',
      target: 'heal',
      stroke: newStroke({ x: uv0[0], y: uv0[1], p: pressure(e) }, { ...ui.brush, flow: 1 }, false),
    };
  }

  if (ui.tool === 'spot') {
    // Simple mode "Remove spots": a tap heals a spot of radius 2% of the short edge, source auto-picked.
    if (!uv0 || settings.heal.length >= MAX_HEAL) return null;
    const r = 0.02 * Math.min(W, H);
    const cx = uv0[0] * W,
      cy = uv0[1] * H;
    const offset = autoHealOffset(ctrl.lumaSampler(cx, cy, 3.5 * r), cx, cy, r, W, H);
    const stroke = newStroke({ x: uv0[0], y: uv0[1], p: 1 }, { size: 0.04, feather: 0.5, flow: 1 }, false);
    ed.update(id, (s) => void s.heal.push(newHealSpot(stroke, offset, 'heal')));
    return null;
  }
  if (ui.tool === 'cropPan') {
    return { kind: 'cropPan', x: px, y: py, crop: { ...settings.geometry.crop } };
  }

  if (ui.tool === 'bgRefine') {
    if (!uv0) return null;
    // Keep adds subject, Remove takes it away (Alt toggles); stored as erase = remove.
    const remove = (ui.refineMode === 'remove') !== e.altKey;
    const stroke = newStroke({ x: uv0[0], y: uv0[1], p: pressure(e) }, ui.brush, remove);
    ed.update(id, (s) => void s.background.refine.push(copyStroke(stroke)), { commit: false });
    return { kind: 'paint', target: 'refine', stroke };
  }

  if (ui.tool === 'skin' || ui.tool === 'cleanWhites') {
    if (!uv0) return null;
    const stroke = newStroke({ x: uv0[0], y: uv0[1], p: pressure(e) }, ui.brush, e.altKey);
    const target = ui.tool;
    ed.update(
      id,
      (s) => {
        if (target === 'skin') {
          s.skin.strokes.push(copyStroke(stroke));
          if (s.skin.amount === 0) s.skin.amount = 50;
        } else {
          s.cleanWhites.strokes = [...(s.cleanWhites.strokes ?? []), copyStroke(stroke)];
          if (s.cleanWhites.amount === 0) s.cleanWhites.amount = 70;
        }
      },
      { commit: false },
    );
    return { kind: 'paint', target, stroke };
  }

  // Mask tool: act on the selected mask.
  const sel = selectedMask(settings, ui);
  if (!sel) return null;
  const { mask, index } = sel;
  if (mask.kind === 'brush') {
    if (!uv0) return null;
    const stroke = newStroke({ x: uv0[0], y: uv0[1], p: pressure(e) }, ui.brush, e.altKey);
    ed.update(
      id,
      (s) => void (s.masks[index]!.geom as { strokes: Stroke[] }).strokes.push(copyStroke(stroke)),
      {
        commit: false,
      },
    );
    return { kind: 'paint', target: 'mask', stroke };
  }
  const start = uv0 ? { x: uv0[0], y: uv0[1] } : null;
  if (mask.kind === 'linear') {
    const g = mask.geom as LinearGeom;
    const a = view({ x: g.x0, y: g.y0 }),
      b = view({ x: g.x1, y: g.y1 });
    const mid = view({ x: (g.x0 + g.x1) / 2, y: (g.y0 + g.y1) / 2 });
    const which =
      dist(a, px, py) < HIT ? 'a' : dist(b, px, py) < HIT ? 'b' : dist(mid, px, py) < HIT ? 'move' : 'new';
    if (!start) return null;
    return { kind: 'linear', which, start, geom: { ...g } };
  }
  const g = mask.geom as RadialGeom;
  const h = radialHandles(g, W, H);
  const which =
    dist(view(h.rx), px, py) < HIT
      ? 'rx'
      : dist(view(h.ry), px, py) < HIT
        ? 'ry'
        : dist(view(h.rot), px, py) < HIT
          ? 'rot'
          : dist(view({ x: g.cx, y: g.cy }), px, py) < HIT * 2
            ? 'move'
            : 'new';
  if (!start) return null;
  return { kind: 'radial', which, start, geom: { ...g } };
}

export function localMove(
  c: LocalCtx,
  d: LocalDrag,
  px: number,
  py: number,
  e: { pointerType: string; pressure: number },
): boolean {
  const { ctrl, id } = c;
  const src = ctrl.sourceSize;
  const uv = ctrl.viewToSource(px, py) ?? clampUv(ctrl, px, py);
  if (!src || !uv) return false;
  const W = src.width,
    H = src.height;
  const p = { x: uv[0], y: uv[1] };
  const ed = useEditor.getState();
  if (d.kind === 'paint') {
    if (!extendStroke(d.stroke, { ...p, p: pressure(e) }, W, H)) return false;
    if (d.target === 'heal') return true; // drawn on the 2D overlay until release
    const copy = copyStroke(d.stroke);
    ed.update(
      id,
      (s) => {
        const list =
          d.target === 'refine'
            ? s.background.refine
            : d.target === 'skin'
              ? s.skin.strokes
              : d.target === 'cleanWhites'
                ? s.cleanWhites.strokes!
                : (s.masks.find((m) => m.id === c.ui.maskId)!.geom as { strokes: Stroke[] }).strokes;
        list[list.length - 1] = copy;
      },
      { commit: false },
    );
    return true;
  }
  if (d.kind === 'cropPan') {
    // Dragging the photo moves it inside the frame: the crop moves the other way.
    const l = ctrl.layout;
    if (!l) return false;
    ed.update(
      id,
      (s) => {
        const c = d.crop;
        const nx = Math.min(1 - c.w, Math.max(0, c.x - ((px - d.x) / l.rect.w) * c.w));
        const ny = Math.min(1 - c.h, Math.max(0, c.y - ((py - d.y) / l.rect.h) * c.h));
        const next = { ...c, x: nx, y: ny };
        if (cropIsCovered({ ...s.geometry, crop: next }, W, H)) s.geometry.crop = next;
      },
      { commit: false },
    );
    return true;
  }
  const dx = p.x - d.start.x,
    dy = p.y - d.start.y;
  if (d.kind === 'healHandle') {
    ed.update(
      id,
      (s) => {
        const sp = s.heal[d.index]!;
        if (d.which === 'src') sp.offset = { x: d.offset.x + dx, y: d.offset.y + dy };
        else {
          sp.stroke = { ...sp.stroke, points: d.points.map((q) => ({ ...q, x: q.x + dx, y: q.y + dy })) };
          sp.offset = { x: d.offset.x - dx, y: d.offset.y - dy }; // the source stays put
        }
      },
      { commit: false },
    );
    return true;
  }
  ed.update(
    id,
    (s) => {
      const m = s.masks.find((k) => k.id === c.ui.maskId);
      if (!m) return;
      if (d.kind === 'linear') {
        const g0 = d.geom;
        m.geom =
          d.which === 'a'
            ? { ...g0, x0: g0.x0 + dx, y0: g0.y0 + dy }
            : d.which === 'b'
              ? { ...g0, x1: g0.x1 + dx, y1: g0.y1 + dy }
              : d.which === 'move'
                ? { x0: g0.x0 + dx, y0: g0.y0 + dy, x1: g0.x1 + dx, y1: g0.y1 + dy }
                : { x0: d.start.x, y0: d.start.y, x1: p.x, y1: p.y };
        return;
      }
      const g0 = d.geom;
      const a = (g0.angle * Math.PI) / 180;
      const vx = (p.x - g0.cx) * W,
        vy = (p.y - g0.cy) * H;
      if (d.which === 'move') m.geom = { ...g0, cx: g0.cx + dx, cy: g0.cy + dy };
      else if (d.which === 'rx')
        m.geom = { ...g0, rx: Math.max(0.005, Math.abs(vx * Math.cos(a) + vy * Math.sin(a)) / W) };
      else if (d.which === 'ry')
        m.geom = { ...g0, ry: Math.max(0.005, Math.abs(-vx * Math.sin(a) + vy * Math.cos(a)) / H) };
      else if (d.which === 'rot') m.geom = { ...g0, angle: (Math.atan2(vx, -vy) * 180) / Math.PI };
      else {
        const r = Math.max(2, Math.hypot((p.x - d.start.x) * W, (p.y - d.start.y) * H));
        m.geom = { ...g0, cx: d.start.x, cy: d.start.y, rx: r / W, ry: r / H, angle: 0 };
      }
    },
    { commit: false },
  );
  return true;
}

function clampUv(ctrl: PreviewController, px: number, py: number): [number, number] | null {
  // Outside the image: keep dragging handles by projecting onto the nearest edge of the view rect.
  const l = ctrl.layout;
  if (!l) return null;
  const x = Math.min(l.rect.x + l.rect.w - 0.5, Math.max(l.rect.x + 0.5, px));
  const y = Math.min(l.rect.y + l.rect.h - 0.5, Math.max(l.rect.y + 0.5, py));
  return ctrl.viewToSource(x, y);
}

export function localUp(c: LocalCtx, d: LocalDrag) {
  const { ctrl, id, ui } = c;
  const ed = useEditor.getState();
  if (d.kind === 'paint' && d.target === 'heal') {
    const src = ctrl.sourceSize;
    if (!src) return;
    const cen = strokeCentre(d.stroke);
    let offset: Uv;
    if (ui.healSource) {
      offset = { x: ui.healSource.x - cen.x, y: ui.healSource.y - cen.y };
      useUi.getState().set({ healSource: null });
    } else {
      const r = (d.stroke.size / 2) * Math.min(src.width, src.height);
      const cx = cen.x * src.width,
        cy = cen.y * src.height;
      offset = autoHealOffset(ctrl.lumaSampler(cx, cy, 3.5 * r), cx, cy, r, src.width, src.height);
    }
    ed.update(id, (s) => void s.heal.push(newHealSpot(copyStroke(d.stroke), offset, ui.healMode)));
    return;
  }
  ed.commit(id);
}

/** Draws handles, the brush cursor and the heal spots on the 2D overlay (CSS px). */
export function drawLocalOverlay(
  ctx: CanvasRenderingContext2D,
  c: Omit<LocalCtx, 'id'>,
  hover: { x: number; y: number } | null,
  drag: LocalDrag | null,
) {
  const { ctrl, settings, ui } = c;
  const src = ctrl.sourceSize;
  if (!src) return;
  const W = src.width,
    H = src.height;
  const view = (u: Uv) => ctrl.sourceToView(u.x, u.y);
  const dot = (p: [number, number] | null, r = 5, fill = cssVar('--on-photo')) => {
    if (!p) return;
    ctx.beginPath();
    ctx.arc(p[0], p[1], r, 0, Math.PI * 2);
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.strokeStyle = cssVar('--on-photo-shadow');
    ctx.lineWidth = 1;
    ctx.stroke();
  };
  ctx.save();
  if (ui.tool === 'mask') {
    const sel = selectedMask(settings, ui);
    if (sel?.mask.kind === 'linear') {
      const g = sel.mask.geom as LinearGeom;
      const dxp = (g.x1 - g.x0) * W,
        dyp = (g.y1 - g.y0) * H;
      const len = Math.hypot(dxp, dyp) || 1;
      const nx = -dyp / len,
        ny = dxp / len; // perpendicular, source px
      const span = Math.max(W, H);
      ctx.strokeStyle = cssVar('--on-photo');
      ctx.lineWidth = 1;
      for (const t of [0, 0.5, 1]) {
        const cx = g.x0 + (g.x1 - g.x0) * t,
          cy = g.y0 + (g.y1 - g.y0) * t;
        const a = view({ x: cx - (nx * span) / W, y: cy - (ny * span) / H }),
          b = view({ x: cx + (nx * span) / W, y: cy + (ny * span) / H });
        if (!a || !b) continue;
        ctx.setLineDash(t === 0.5 ? [] : [6, 4]);
        ctx.beginPath();
        ctx.moveTo(a[0], a[1]);
        ctx.lineTo(b[0], b[1]);
        ctx.stroke();
      }
      ctx.setLineDash([]);
      dot(view({ x: g.x0, y: g.y0 }));
      dot(view({ x: g.x1, y: g.y1 }));
      dot(view({ x: (g.x0 + g.x1) / 2, y: (g.y0 + g.y1) / 2 }), 4, cssVar('--photo-guide'));
    } else if (sel?.mask.kind === 'radial') {
      const g = sel.mask.geom as RadialGeom;
      ctx.strokeStyle = cssVar('--on-photo');
      ctx.lineWidth = 1.5;
      for (const k of [1, 1 - g.feather]) {
        if (k <= 0.01) continue;
        ctx.setLineDash(k === 1 ? [] : [5, 4]);
        ctx.beginPath();
        for (let i = 0; i <= 64; i++) {
          const t = (i / 64) * Math.PI * 2;
          const q = view(radialPoint(g, W, H, g.rx * W * k * Math.cos(t), g.ry * H * k * Math.sin(t)));
          if (!q) continue;
          if (i === 0) ctx.moveTo(q[0], q[1]);
          else ctx.lineTo(q[0], q[1]);
        }
        ctx.stroke();
      }
      ctx.setLineDash([]);
      const h = radialHandles(g, W, H);
      dot(view({ x: g.cx, y: g.cy }), 5, cssVar('--photo-guide'));
      dot(view(h.rx));
      dot(view(h.ry));
      dot(view(h.rot), 4, cssVar('--photo-source'));
    }
  }
  if (ui.tool === 'heal') {
    settings.heal.forEach((sp) => {
      const cen = strokeCentre(sp.stroke);
      const sc = { x: cen.x + sp.offset.x, y: cen.y + sp.offset.y };
      const a = view(cen),
        b = view(sc);
      const r = screenRadius(ctrl, cen, sp.stroke.size);
      if (!a || !b) return;
      ctx.strokeStyle = cssVar('--brush-ring');
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(a[0], a[1], r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.arc(b[0], b[1], r, 0, Math.PI * 2);
      ctx.moveTo(a[0], a[1]);
      ctx.lineTo(b[0], b[1]);
      ctx.stroke();
      ctx.setLineDash([]);
    });
    if (ui.healSource) dot(view(ui.healSource), 4, cssVar('--photo-source'));
    if (drag?.kind === 'paint' && drag.target === 'heal') {
      ctx.fillStyle = cssVar('--brush-fill');
      for (const q of drag.stroke.points) {
        const v = view(q);
        if (!v) continue;
        ctx.beginPath();
        ctx.arc(v[0], v[1], screenRadius(ctrl, q, drag.stroke.size), 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
  const brushTool =
    ui.tool === 'heal' ||
    ui.tool === 'skin' ||
    ui.tool === 'cleanWhites' ||
    ui.tool === 'bgRefine' ||
    (ui.tool === 'mask' && selectedMask(settings, ui)?.mask.kind === 'brush');
  if (brushTool && hover) {
    const uv = ctrl.viewToSource(hover.x, hover.y);
    if (uv) {
      const r = screenRadius(ctrl, { x: uv[0], y: uv[1] }, ui.brush.size);
      ctx.strokeStyle = cssVar('--on-photo');
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(hover.x, hover.y, r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.arc(hover.x, hover.y, r * (1 - ui.brush.feather), 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }
  ctx.restore();
}

/** Red overlay to draw (O, or the brush being used): masks + index, or null. */
export function overlayFor(s: Settings, ui: UiState): { masks: LocalMask[]; index: number } | null {
  if (ui.tool === 'skin' && s.skin.strokes.length) return { masks: [brushMask(s.skin.strokes)], index: 0 };
  if (ui.tool === 'cleanWhites' && s.cleanWhites.strokes?.length)
    return { masks: [brushMask(s.cleanWhites.strokes)], index: 0 };
  if (!ui.maskOverlay) return null;
  const sel = selectedMask(s, ui);
  return sel ? { masks: s.masks, index: sel.index } : null;
}
const brushMask = (strokes: Stroke[]): LocalMask => ({
  id: '_brush',
  kind: 'brush',
  geom: { strokes },
  invert: false,
  adj: { exposure: 0, contrast: 0, temp: 0, tint: 0, saturation: 0, highlights: 0, shadows: 0 },
});
